import { useCallback, useEffect, useRef, useState } from "react"

import { Visibility, FilterList, Clear } from '@mui/icons-material'
import {
  Box,
  Typography,
  Alert,
  Chip,
  Button,
  Skeleton,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Stack,
  Collapse,
  IconButton,
  Tooltip,
} from '@mui/material'
import { type GridColDef, type GridPaginationModel, type GridRowParams } from '@mui/x-data-grid'
import { useNavigate } from "react-router-dom"

import { formatPrice } from "@/lib/utils"

import { handleApiError, ordersService, ORDER_STATUSES, type Order, type LabRecommendationsAdminResponse, type AIQCReview, type OrderStatus, type GetOrdersParams } from "../api"
import { AdminDataGrid } from "../components/admin-data-grid"
import { LinkCell, DateCell, StatusChip } from "../components/data-grid-cells"
import { truncateId } from "../components/data-grid-utils"
import { CreateOrderDialog } from "../components/orders/create-order-dialog"
import PSCAppointmentHeaderInfo from "../components/orders/psc-appointment-header-info"
import StatusHeaderInfo from "../components/orders/status-header-info"
import { useUser } from "../hooks/use-user"
import { canCreateOrders } from "../lib/user-utils"

type OrderWithRecommendations = Order & {
  recommendations?: LabRecommendationsAdminResponse | null
  qcReview?: AIQCReview | null
}

const DEFAULT_PAGE_SIZE = 50


type OrderFilters = {
  status: OrderStatus | ''
  dateFrom: string
  dateTo: string
}

const EMPTY_FILTERS: OrderFilters = {
  status: '',
  dateFrom: '',
  dateTo: '',
}

const getStatusColor = (status: string) => {
  switch (status.toLowerCase()) {
    case 'completed': return 'success' as const
    case 'in_progress': return 'primary' as const
    case 'received': return 'secondary' as const
    case 'created': return 'info' as const
    case 'purchased': return 'warning' as const
    case 'failed': return 'error' as const
    case 'cancelled': return 'error' as const
    case 'deleted': return 'default' as const
    default: return 'default' as const
  }
}

const getAppointmentStatusColor = (status: string | null) => {
  if (!status) return 'default' as const
  switch (status.toLowerCase()) {
    case 'scheduled':
    case 'confirmed': return 'success' as const
    case 'cancelled': return 'error' as const
    case 'completed': return 'primary' as const
    default: return 'default' as const
  }
}

export default function OrdersPage() {
  const [orders, setOrders] = useState<OrderWithRecommendations[]>([])
  const [totalOrders, setTotalOrders] = useState(0)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState("")
  const [filtersOpen, setFiltersOpen] = useState(false)
  const [filters, setFilters] = useState<OrderFilters>(EMPTY_FILTERS)
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: DEFAULT_PAGE_SIZE,
  })
  const navigate = useNavigate()
  const { user: currentUser } = useUser()
  const fetchIdRef = useRef(0)

  const hasActiveFilters = filters.status !== '' || filters.dateFrom !== '' || filters.dateTo !== ''

  const fetchOrders = useCallback(async (page: number, pageSize: number, currentFilters: OrderFilters) => {
    const fetchId = ++fetchIdRef.current
    try {
      setIsLoading(true)
      setError("")
      const params: GetOrdersParams = {
        page: page + 1,
        limit: pageSize,
      }

      if (currentFilters.status) {
        params.status = currentFilters.status
      }
      if (currentFilters.dateFrom) {
        params.dateFrom = new Date(currentFilters.dateFrom).toISOString()
      }
      if (currentFilters.dateTo) {
        params.dateTo = new Date(`${currentFilters.dateTo}T23:59:59`).toISOString()
      }

      const response = await ordersService.getOrders(params)
      if (fetchIdRef.current !== fetchId) return
      setOrders(response.items)
      setTotalOrders(response.total)
    } catch (err) {
      if (fetchIdRef.current !== fetchId) return
      setError(handleApiError(err))
    } finally {
      if (fetchIdRef.current === fetchId) setIsLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchOrders(paginationModel.page, paginationModel.pageSize, filters)
  }, [paginationModel, filters, fetchOrders])

  const handleClearFilters = () => {
    setFilters(EMPTY_FILTERS)
    setPaginationModel(prev => ({ ...prev, page: 0 }))
  }

  const handleRowClick = (params: GridRowParams) => {
    navigate(`/orders/${params.id}`)
  }

  const columns: GridColDef[] = [
    {
      field: 'id',
      headerName: 'Order ID',
      flex: 0.8,
      minWidth: 160,
      renderCell: (params) => (
        <LinkCell to={`/orders/${params.value as string}`} monospace>
          {truncateId(params.value as string)}
        </LinkCell>
      ),
    },
    {
      field: 'userEmail',
      headerName: 'User Email',
      flex: 1,
      minWidth: 200,
      valueGetter: (_, row) => row.user?.email,
      renderCell: (params) => (
        <LinkCell to={`/users/${params.row.user?.id}`}>
          {params.value}
        </LinkCell>
      ),
    },
    {
      field: 'packageId',
      headerName: 'Package ID',
      flex: 0.8,
      minWidth: 160,
      renderCell: (params) => (
        params.value ? (
          <LinkCell to={`/orders/${params.row.id}`} monospace>
            {params.value}
          </LinkCell>
        ) : (
          <Typography variant="body2" color="text.secondary">-</Typography>
        )
      ),
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 140,
      renderHeader: () => (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          Status
          <StatusHeaderInfo />
        </Box>
      ),
      renderCell: (params) => (
        <StatusChip
          label={params.value.toUpperCase()}
          color={getStatusColor(params.value)}
        />
      ),
    },
    {
      field: 'pscAppointmentStatus',
      headerName: 'PSC Appointment',
      flex: 0.8,
      minWidth: 200,
      renderHeader: () => (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          PSC Appointment
          <PSCAppointmentHeaderInfo />
        </Box>
      ),
      renderCell: (params) => (
        <StatusChip
          label={params.value ? params.value.toUpperCase() : 'NONE'}
          color={getAppointmentStatusColor(params.value)}
        />
      ),
    },
    {
      field: 'price',
      headerName: 'Price',
      width: 120,
      renderCell: (params) => {
        const price = params.value
        const currency = params.row.currency
        return price !== null && currency ? (
          <Typography variant="body2" fontWeight={500}>
            {formatPrice(price, currency)}
          </Typography>
        ) : (
          <Typography variant="body2" color="text.secondary">-</Typography>
        )
      },
    },
    {
      field: 'createdAt',
      headerName: 'Created',
      flex: 0.8,
      minWidth: 160,
      renderCell: (params) => (
        <DateCell value={params.value as string} stacked />
      ),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 120,
      sortable: false,
      renderCell: (params) => (
        <Button
          size="small"
          variant="outlined"
          startIcon={<Visibility />}
          onClick={(e) => {
            e.stopPropagation()
            navigate(`/orders/${params.row.id}`)
          }}
        >
          View
        </Button>
      ),
    },
  ]

  if (isLoading && orders.length === 0) {
    return (
      <Box sx={{ flex: 1, p: 3 }}>
        <Card sx={{ height: 'fit-content' }}>
          <CardContent sx={{ p: 3 }}>
            <Box sx={{ mb: 3 }}>
              <Skeleton variant="text" width={300} height={40} />
            </Box>
            <Box sx={{ height: 400 }}>
              <Skeleton variant="rectangular" width="100%" height="100%" />
            </Box>
          </CardContent>
        </Card>
      </Box>
    )
  }

  return (
    <Box sx={{ flex: 1, p: 3 }}>
      <Card sx={{ height: 'fit-content' }}>
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="h4" component="h1" fontWeight="bold">
                Lab Orders ({totalOrders} total)
              </Typography>
              <Tooltip title={filtersOpen ? "Hide filters" : "Show filters"}>
                <IconButton
                  onClick={() => setFiltersOpen(prev => !prev)}
                  color={hasActiveFilters ? "primary" : "default"}
                  size="small"
                >
                  <FilterList />
                </IconButton>
              </Tooltip>
              {hasActiveFilters && (
                <Chip
                  label="Filtered"
                  size="small"
                  color="primary"
                  variant="outlined"
                  onDelete={handleClearFilters}
                />
              )}
            </Box>
            {canCreateOrders(currentUser) && <CreateOrderDialog onOrderCreated={() => {
              setPaginationModel(prev => ({ ...prev, page: 0 }))
              fetchOrders(0, paginationModel.pageSize, filters)
            }} />}
          </Box>

          <Collapse in={filtersOpen}>
            <Card variant="outlined" sx={{ mb: 2, p: 2 }}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
                <FormControl size="small" sx={{ minWidth: 160 }}>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={filters.status}
                    label="Status"
                    onChange={(e) => {
                      setFilters(prev => ({ ...prev, status: e.target.value as OrderStatus | '' }))
                      setPaginationModel(prev => ({ ...prev, page: 0 }))
                    }}
                  >
                    <MenuItem value="">All statuses</MenuItem>
                    {ORDER_STATUSES.map(status => (
                      <MenuItem key={status} value={status}>
                        {status.toUpperCase().replace('_', ' ')}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <TextField
                  size="small"
                  label="From date"
                  type="date"
                  value={filters.dateFrom}
                  onChange={(e) => {
                    setFilters(prev => ({ ...prev, dateFrom: e.target.value }))
                    setPaginationModel(prev => ({ ...prev, page: 0 }))
                  }}
                  slotProps={{ inputLabel: { shrink: true } }}
                  sx={{ minWidth: 160 }}
                />

                <TextField
                  size="small"
                  label="To date"
                  type="date"
                  value={filters.dateTo}
                  onChange={(e) => {
                    setFilters(prev => ({ ...prev, dateTo: e.target.value }))
                    setPaginationModel(prev => ({ ...prev, page: 0 }))
                  }}
                  slotProps={{ inputLabel: { shrink: true } }}
                  sx={{ minWidth: 160 }}
                />

                {hasActiveFilters && (
                  <Button
                    size="small"
                    variant="text"
                    startIcon={<Clear />}
                    onClick={handleClearFilters}
                  >
                    Clear
                  </Button>
                )}
              </Stack>
            </Card>
          </Collapse>

          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          <Card variant="outlined" sx={{ mt: 2 }}>
            <CardContent sx={{ p: 0 }}>
              <AdminDataGrid
                rows={orders}
                columns={columns}
                rowHeight={80}
                rowCount={totalOrders}
                paginationMode="server"
                paginationModel={paginationModel}
                onPaginationModelChange={setPaginationModel}
                pageSizeOptions={[25, 50, 100]}
                loading={isLoading}
                onRowClick={handleRowClick}
              />
            </CardContent>
          </Card>
        </CardContent>
      </Card>

    </Box>
  )
}
